import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useRfq } from "./RfqContext";

const LINKS = [
  { to: "/products", label: "Products" },
  { to: "/industries", label: "Industries" },
  { to: "/#infrastructure", label: "Infrastructure" },
  { to: "/#quality", label: "Quality" },
  { to: "/about", label: "About" },
  { to: "/#downloads", label: "Downloads" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { open: openRfq } = useRfq();
  const loc = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "border-b border-slate-800/80 bg-[#07090e]/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="container-x flex h-[72px] items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
        <nav className="hidden items-center gap-6 2xl:gap-8 xl:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="group relative text-[13px] font-medium uppercase tracking-[0.12em] text-slate-200 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-sky-400 transition-transform duration-300 group-hover:scale-x-100" />
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid="nav-get-quote-button" onClick={() => openRfq()} className="btn-electric whitespace-nowrap !px-4 !py-2.5 !text-xs sm:!px-5 sm:!text-sm">
            <span className="sm:hidden">Quote</span><span className="hidden sm:inline">Get a Quote</span> <ArrowUpRight className="hidden h-4 w-4 sm:block" />
          </button>
          <button data-testid="nav-mobile-toggle" aria-label="Menu" onClick={() => setOpen((v) => !v)} className="p-2 text-white xl:hidden">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-slate-800 xl:hidden"
          >
            <div className="container-x flex flex-col py-4">
              {LINKS.map((l, i) => (
                <Link key={l.to} to={l.to} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-display flex items-center justify-between border-b border-slate-800/70 py-4 text-3xl font-bold uppercase text-white">
                  {l.label}<span className="font-mono-x text-xs text-slate-400">0{i + 1}</span>
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
